import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useNotice = () => {
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/privacy-notice`)
      .then((r) => r.json())
      .then(setNotice)
      .catch(() => setError(true));
  }, []);
  return { notice, error };
};

export const NoticeContent = ({ notice }) => {
  if (!notice) return <p className="text-slate-500 text-sm">Loading privacy notice…</p>;
  return (
    <div className="space-y-2 text-sm text-slate-700" data-testid="privacy-notice-content">
      {notice.content.split('\n').map((line, i) => {
        const t = line.trim();
        if (!t) return null;
        if (t.startsWith('## ')) return <h2 key={i} className="text-lg font-semibold text-slate-900 pt-3">{t.slice(3)}</h2>;
        if (t.startsWith('- ')) return <li key={i} className="ml-5 list-disc">{t.slice(2).replace(/\*\*/g, '')}</li>;
        return <p key={i}>{t.replace(/\*\*(.*?)\*\*/g, '$1')}</p>;
      })}
    </div>
  );
};

// Full-page version used at /privacy (public — linked from registration/login/signing)
export const PrivacyNoticeStandalone = () => {
  const { notice, error } = useNotice();
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Privacy Notice
              {notice && <Badge variant="secondary">v{notice.version}</Badge>}
            </CardTitle>
            {notice && <p className="text-xs text-slate-500">Published {new Date(notice.published_at).toLocaleDateString('en-GB')}</p>}
          </CardHeader>
          <CardContent>
            {error ? <p className="text-sm text-red-600">Could not load the privacy notice. Please try again later.</p> : <NoticeContent notice={notice} />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Modal version (footer link inside the app)
const PrivacyPolicy = ({ onClose }) => {
  const { notice, error } = useNotice();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <Card>
          <CardHeader className="border-b sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl flex items-center gap-2">
                Privacy Notice {notice && <Badge variant="secondary">v{notice.version}</Badge>}
              </CardTitle>
              <Button variant="ghost" onClick={onClose} data-testid="close-privacy-policy">×</Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {error ? <p className="text-sm text-red-600">Could not load the privacy notice.</p> : <NoticeContent notice={notice} />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
